import React, { useState, useEffect } from 'react';
import { Box, CssBaseline, AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemIcon, ListItemText, useTheme, useMediaQuery, Divider, Tooltip, Avatar, Menu, MenuItem, Button, Chip, Popover, Paper, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Badge } from '@mui/material';
import axios from 'axios';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ImageIcon from '@mui/icons-material/Image';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ScienceIcon from '@mui/icons-material/Science';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';
import { useColorMode } from '../../theme/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import SessionWarning from '../common/SessionWarning';
import AITradeModal from '../ai-trade/AITradeModal';
import { showTawkChat, hideTawkChat } from '../TawkChatWidget';

const drawerWidthOpen = 240;
const drawerWidthClosed = 72;

const AppLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [subscriptionAnchorEl, setSubscriptionAnchorEl] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistInterests, setWaitlistInterests] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const navigate = useNavigate();
  const { toggleColorMode } = useColorMode();
  const { user, logout, isAuthenticated } = useAuth();
  const { settings } = useSettings();

  useEffect(() => {
    if (user?.attributes?.email) {
      setWaitlistEmail(user.attributes.email);
    }
    
    // Set up the global function to update chat state
    window.updateChatState = (isOpen) => {
      setIsChatOpen(isOpen);
    };
    
    return () => {
      window.updateChatState = null;
    };
  }, [user]);
  
  // Close drawer on mobile when window resizes
  useEffect(() => {
    if (isMobile) {
      setIsDrawerOpen(false);
    }
  }, [isMobile]);

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleSubscriptionClick = (event) => {
    setSubscriptionAnchorEl(event.currentTarget);
  };
  
  const handleSubscriptionClose = () => {
    setSubscriptionAnchorEl(null);
  };
  
  const handleUpgradeClick = () => {
    setUpgradeModalOpen(true);
  };
  
  const handleWaitlistSubmit = async () => {
    try {
      setWaitlistSubmitting(true);
      const token = localStorage.getItem('idToken');
      
      // Get current settings first
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/usersettings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const currentSettings = response.data || {};
      
      // Add waitlist information to settings
      await axios.post(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/usersettings',
        {
          settings: {
            ...currentSettings,
            waitlist: {
              email: waitlistEmail,
              interests: waitlistInterests,
              joinedAt: new Date().toISOString()
            }
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUpgradeModalOpen(false);
      alert('Thanks for your interest! We\'ll notify you when premium plans are available.');
    } catch (error) {
      console.error('Error saving waitlist information:', error);
      alert('There was an error joining the waitlist. Please try again.');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/auth');
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleSettings = () => {
    handleMenuClose();
    navigate('/settings');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon sx={{ color: '#2196F3' }} />, path: '/' },
    { text: 'Watchlist', icon: <VisibilityIcon sx={{ color: '#9C27B0' }} />, path: '/watchlist' },
    { text: 'Calendar', icon: <CalendarTodayIcon sx={{ color: '#FF9800' }} />, path: '/calendar' },

    { text: 'Journal Entry(AI)', icon: <AutoAwesomeIcon sx={{ color: '#9C27B0' }} />, path: '/ai-trade-create' },
    { text: 'All Trades', icon: <PlaylistAddIcon sx={{ color: '#4CAF50' }} />, path: '/add-trade' },

    { text: 'Strategy Lab', icon: <ScienceIcon sx={{ color: '#E91E63' }} />, path: '/create-strategy' },
    { text: 'Trade Feedback & Coaching', icon: <FeedbackIcon sx={{ color: '#00BCD4' }} />, path: '/ai-feedback' },
    { text: 'Import Trades (CSV)', icon: <UploadFileIcon sx={{ color: '#FF5722' }} />, path: '/upload-csv' },
  ];
  
  const accountItems = [
    { text: 'Profile', icon: <AccountCircleIcon sx={{ color: '#673AB7' }} />, path: '/profile' },
    { text: 'Settings', icon: <SettingsIcon sx={{ color: '#607D8B' }} />, path: '/settings' },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <SessionWarning />
      <CssBaseline />

      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={toggleDrawer} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <img 
            src={`${process.env.PUBLIC_URL}/logo192.png`} 
            alt="Logo" 
            style={{ width: 32, height: 32, borderRadius: '6px', marginRight: 12 }}
          />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            The Journal App
          </Typography>
          <Box display="flex" alignItems="center" sx={{ gap: { xs: 0.5, sm: 1 } }}>
            {settings?.subscription && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                <Tooltip title="Click to view subscription details">
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    onClick={handleSubscriptionClick}
                    sx={{ 
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      py: { xs: 0.5, sm: 1 },
                      px: { xs: 1, sm: 2 },
                      height: { xs: 28, sm: 36 },
                      borderRadius: 2,
                      minWidth: 'auto',
                      borderColor: settings.subscription.subscriptionPlan === 'free' ? 'grey.400' : 'primary.main',
                      color: settings.subscription.subscriptionPlan === 'free' ? 'text.secondary' : 'primary.main'
                    }}
                  >
                    {settings.subscription.subscriptionPlan}
                  </Button>
                </Tooltip>
                {settings.subscription.subscriptionPlan === 'free' && (
                  <Button 
                    size="small" 
                    variant="contained" 
                    color="primary"
                    onClick={handleUpgradeClick}
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      py: { xs: 0.5, sm: 1 },
                      px: { xs: 1, sm: 2 },
                      height: { xs: 28, sm: 36 },
                      borderRadius: 2,
                      minWidth: 'auto',
                      textTransform: 'none'
                    }}
                  >
                    Upgrade
                  </Button>
                )}
              </Box>
            )}
            <Button
              onClick={() => setAiModalOpen(true)}
              variant="contained"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />}
              sx={{ 
                bgcolor: '#1976D2',
                color: 'white',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                borderRadius: 2,
                fontWeight: 'bold',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                textTransform: 'none',
                height: { xs: 28, sm: 36 },
                boxShadow: '0 4px 6px rgba(25, 118, 210, 0.3)',
                '&:hover': { 
                  bgcolor: '#1565C0',
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)'
                },
                mr: 1
              }}
            >
              {isMobile ? 'AI Creator' : 'Journal Entry(AI)'}
            </Button>
            <Tooltip title="Live Support">
              <Button
                onClick={() => {
                  if (isChatOpen) {
                    hideTawkChat();
                  } else {
                    showTawkChat();
                  }
                  setIsChatOpen(!isChatOpen);
                }}
                variant="outlined"
                color={isChatOpen ? "primary" : "success"}
                startIcon={
                  <Badge color="success" variant="dot">
                    <SupportAgentIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />
                  </Badge>
                }
                sx={{ 
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  borderRadius: 2,
                  fontWeight: 'bold',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  textTransform: 'none',
                  height: { xs: 28, sm: 36 },
                  mr: 1,
                  borderColor: isChatOpen ? 'primary.main' : 'success.main',
                  color: isChatOpen ? 'primary.main' : 'success.main',
                  '&:hover': { 
                    bgcolor: isChatOpen ? 'primary.light' : 'success.light',
                    color: isChatOpen ? 'primary.contrastText' : 'success.contrastText'
                  }
                }}
              >
                {isMobile ? '' : (isChatOpen ? 'Close Support' : 'Support')}
              </Button>
            </Tooltip>

            <Tooltip title="Toggle Theme">
              <IconButton 
                onClick={toggleColorMode}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  color: theme.palette.mode === 'dark' ? '#FFC107' : '#FF9800',
                  '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 152, 0, 0.1)' }
                }}
              >
                {theme.palette.mode === 'dark' ? 
                  <LightModeIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} /> : 
                  <DarkModeIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                }
              </IconButton>
            </Tooltip>
            
            {isAuthenticated && (
              <>
                <Tooltip title="Account settings">
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    size="small"
                    sx={{ ml: { xs: 0.5, sm: 1 } }}
                    aria-controls="profile-menu"
                    aria-haspopup="true"
                  >
                    <Avatar sx={{ 
                      width: { xs: 28, sm: 32 }, 
                      height: { xs: 28, sm: 32 }, 
                      bgcolor: 'primary.main',
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      {user?.attributes?.given_name ? user.attributes.given_name[0] : 'U'}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  id="profile-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '& .MuiAvatar-root': {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={handleProfile}>
                    <AccountCircleIcon fontSize="small" sx={{ mr: 1 }} /> Profile
                  </MenuItem>
                  <MenuItem onClick={handleSettings}>
                    <SettingsIcon fontSize="small" sx={{ mr: 1 }} /> Settings
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isDrawerOpen}
        onClose={isMobile ? () => setIsDrawerOpen(false) : undefined}
        sx={{
          width: isDrawerOpen ? drawerWidthOpen : drawerWidthClosed,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isDrawerOpen ? drawerWidthOpen : drawerWidthClosed,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: 'width 0.3s',
          },
        }}
      >
        <Toolbar />
        <List>
          {menuItems.map(({ text, icon, path, premium }) => {
            const isPremium = settings?.subscription?.subscriptionPlan === 'premium';
            const isDisabled = premium && !isPremium;
            
            return (
              <ListItem 
                button 
                key={text} 
                onClick={() => navigate(path)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                <Tooltip title={!isDrawerOpen ? text : ''} placement="right">
                  <ListItemIcon sx={{ minWidth: 0, mr: isDrawerOpen ? 2 : 'auto', justifyContent: 'center' }}>{icon}</ListItemIcon>
                </Tooltip>
                {isDrawerOpen && <ListItemText primary={text} />}
              </ListItem>
            );
          })}
        </List>
        <Divider />
        
        {isAuthenticated && (
          <>
            {isDrawerOpen && (
              <Box px={2} py={1}>
                <Typography variant="body2" color="text.secondary">
                  Account
                </Typography>
              </Box>
            )}
            <List>
              {accountItems.map(({ text, icon, path }) => (
                <ListItem 
                  button 
                  key={text} 
                  onClick={() => navigate(path)}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    mb: 0.5,
                    '&:hover': {
                      bgcolor: 'action.hover',
                      transform: 'translateX(4px)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                >
                  <Tooltip title={!isDrawerOpen ? text : ''} placement="right">
                    <ListItemIcon sx={{ minWidth: 0, mr: isDrawerOpen ? 2 : 'auto', justifyContent: 'center' }}>{icon}</ListItemIcon>
                  </Tooltip>
                  {isDrawerOpen && <ListItemText primary={text} />}
                </ListItem>
              ))}
              <ListItem 
                button 
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  '&:hover': {
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    transform: 'translateX(4px)',
                    transition: 'all 0.2s ease'
                  }
                }}
              >
                <Tooltip title={!isDrawerOpen ? 'Logout' : ''} placement="right">
                  <ListItemIcon sx={{ minWidth: 0, mr: isDrawerOpen ? 2 : 'auto', justifyContent: 'center' }}>
                    <LogoutIcon sx={{ color: '#F44336' }} />
                  </ListItemIcon>
                </Tooltip>
                {isDrawerOpen && <ListItemText primary="Logout" />}
              </ListItem>
            </List>
          </>
        )}
        
        <Box sx={{ mt: 'auto', p: 2, textAlign: 'center' }}>
          <img 
            src={`${process.env.PUBLIC_URL}/logo192.png`} 
            alt="Logo" 
            style={{ width: isDrawerOpen ? 48 : 32, height: isDrawerOpen ? 48 : 32, borderRadius: '8px' }}
          />
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {children}
      </Box>

      <AITradeModal 
        open={aiModalOpen} 
        onClose={() => setAiModalOpen(false)}
        onTradeCreated={() => {
          // Optionally refresh data or show success message
          console.log('Trade created successfully!');
        }}
      />
      
      {/* Subscription Details Popover */}
      <Popover
        open={Boolean(subscriptionAnchorEl)}
        anchorEl={subscriptionAnchorEl}
        onClose={handleSubscriptionClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Paper sx={{ p: 3, maxWidth: 320 }}>
          <Typography variant="h6" gutterBottom>
            {settings?.subscription?.subscriptionPlan?.toUpperCase()} PLAN
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {/* Trade Quota */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Trade Quota
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                Used: {settings?.subscription?.tradeQuota?.used || 0}/{settings?.subscription?.tradeQuota?.totalLimit || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round((settings?.subscription?.tradeQuota?.used || 0) / (settings?.subscription?.tradeQuota?.totalLimit || 1) * 100)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(100, ((settings?.subscription?.tradeQuota?.used || 0) / (settings?.subscription?.tradeQuota?.totalLimit || 1) * 100))}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
          
          {/* AI Trade Assist Quota */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              AI Trade Analysis
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                Used: {settings?.subscription?.aiTradeAssistQuota?.used || 0}/{settings?.subscription?.aiTradeAssistQuota?.totalLimit || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round((settings?.subscription?.aiTradeAssistQuota?.used || 0) / (settings?.subscription?.aiTradeAssistQuota?.totalLimit || 1) * 100)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(100, ((settings?.subscription?.aiTradeAssistQuota?.used || 0) / (settings?.subscription?.aiTradeAssistQuota?.totalLimit || 1) * 100))}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
          
          {/* AI Feedback Quota */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Trade Feedback & Coaching
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                Used: {settings?.subscription?.aiFeedbackQuota?.used || 0}/{settings?.subscription?.aiFeedbackQuota?.totalLimit || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round((settings?.subscription?.aiFeedbackQuota?.used || 0) / (settings?.subscription?.aiFeedbackQuota?.totalLimit || 1) * 100)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(100, ((settings?.subscription?.aiFeedbackQuota?.used || 0) / (settings?.subscription?.aiFeedbackQuota?.totalLimit || 1) * 100))}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
          
          {settings?.subscription?.subscriptionEndDate && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Expires: {new Date(settings.subscription.subscriptionEndDate).toLocaleDateString()}
            </Typography>
          )}
        </Paper>
      </Popover>
      
      {/* Upgrade Plan Modal */}
      <Dialog
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upgrade Your Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Premium plans are coming soon! Join our waitlist to get early access and special discounts.
          </Typography>
          <TextField
            label="Email Address"
            variant="outlined"
            fullWidth
            margin="normal"
            value={waitlistEmail}
            onChange={(e) => setWaitlistEmail(e.target.value)}
            required
          />
          <TextField
            label="What features are you most interested in?"
            variant="outlined"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            placeholder="Tell us what you're looking for in a premium plan..."
            value={waitlistInterests}
            onChange={(e) => setWaitlistInterests(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpgradeModalOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleWaitlistSubmit}
            disabled={waitlistSubmitting || !waitlistEmail}
          >
            {waitlistSubmitting ? 'Submitting...' : 'Join Waitlist'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppLayout;
